import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { addDatadogAction } from '@cartwhl/tracking/datadog';
import { getStudioUrl } from '@cartwhl/common/utils';
import { DATADOG_ACTIONS } from '@cartwhl/tracking/datadogConstants';
import { start } from '@cartwhl/common/utils/getStartedCookie';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import BorderedCheckmark from '@cartwhl/ui/icons/BorderedCheckmark';
import GoogleIcon from '@cartwhl/ui/icons/GoogleIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';

import { buildRedirectUrl } from '@cartwhl/common/stores/authStore';

import { authStore } from 'stores/global';

import AuthPageLayout from './AuthPageLayout';

export default function SignupPage() {
  const [loading, setLoading] = useState(false);
  const [isPasswordEmpty, setIsPasswordEmpty] = useState(true);
  const [isPasswordLengthMet, setIsPasswordLengthMet] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  useEffect(start, []);

  function handlePasswordOnChange(password: string) {
    setIsPasswordEmpty(password.length === 0);
    setShowPasswordRequirements(true);

    // Check if the password is valid. Right now, we only check the length.
    // Add more validation rules here if needed
    setIsPasswordLengthMet(password.length >= 10);
  }

  function handlePasswordOnBlur() {
    setShowPasswordRequirements(!isPasswordEmpty);
  }

  return (
    <AuthPageLayout title="Sign Up">
      <form
        className="grid grid-cols-1 gap-x-4 gap-y-6 pb-7 md:grid-cols-2"
        onSubmit={async (e) => {
          e.preventDefault();
          const email = e.currentTarget.email.value;
          const password = e.currentTarget.password.value;
          const firstname = e.currentTarget.firstname.value;
          const lastname = e.currentTarget.lastname.value;
          setLoading(true);
          const result = await authStore.signupWithPassword(
            firstname,
            lastname,
            email,
            password,
          );
          if (result) {
            addDatadogAction(DATADOG_ACTIONS.JOIN_NOW);
            // we used to wait to redirect them so that users could see a message about verifying their email.
            // we don't do that anymore and so now we redirect them immediately.
            const redirectTo = new URLSearchParams(window.location.search).get(
              'redirectTo',
            );
            if (redirectTo) {
              window.location.href = buildRedirectUrl(redirectTo);
            } else {
              const studioURL = getStudioUrl();
              window.location.href = studioURL;
            }
          }
          setLoading(false);
        }}
      >
        <Input
          label="First name"
          name="firstname"
          placeholder="First"
          autoComplete="given-name"
        />
        <Input
          label="Last name"
          name="lastname"
          placeholder="Last"
          autoComplete="family-name"
        />
        <Input
          label="Your email"
          type="email"
          name="email"
          placeholder="Email"
          autoComplete="email"
          className="md:col-span-2"
        />
        <Input
          label="Create a password"
          name="password"
          type="password"
          placeholder="Password"
          autoComplete="new-password"
          className="md:col-span-2"
          onChange={(e) => handlePasswordOnChange(e.target.value)}
          onBlur={() => handlePasswordOnBlur()}
        />
        {showPasswordRequirements && (
          <div className="-mt-4 flex items-center md:col-span-2">
            {isPasswordLengthMet ? (
              <BorderedCheckmark className="text-green mr-2" />
            ) : (
              <WarningIcon className="text-gray-45 mr-2" />
            )}
            <Text
              variant="web/body/md"
              className={isPasswordLengthMet ? 'text-gray-45' : 'text-red'}
            >
              Must be at least 10 characters long
            </Text>
          </div>
        )}
        <GoButton
          data-dd-action-name={DATADOG_ACTIONS.JOIN_NOW}
          loading={loading}
          className={`md:col-span-2 ${showPasswordRequirements ? 'mt-3' : 'mt-8'}`}
        >
          Join now
        </GoButton>
      </form>
      <Text
        as="div"
        variant="studio/body/sm"
        className="text-gray-45 mb-3 text-center"
      >
        Have an account?{' '}
        <Link
          to={`/login?${new URLSearchParams(
            window.location.search,
          ).toString()}`}
          className="text-green-50 hover:underline"
        >
          Sign in
        </Link>
      </Text>
      <div className="relative mb-[22px] text-center">
        <hr className="bg-gray-30 absolute top-1/2 right-0 left-0 -z-10 h-px border-none" />
        <Text
          variant="web/body/md"
          className="text-gray-45 dark:bg-gray-90 bg-white px-1"
        >
          or
        </Text>
      </div>
      <div className="mb-6 flex justify-center">
        <Button
          data-dd-action-name={DATADOG_ACTIONS.CONTINUE_WITH_GOOGLE}
          variant="primary"
          icon={<GoogleIcon />}
          onClick={authStore.continueWithGoogle}
        >
          Continue with Google
        </Button>
      </div>
    </AuthPageLayout>
  );
}
